import {memo, useCallback, useMemo} from 'react'
import {useLocation} from 'react-router-dom'

import useDocumentTitle from '../../../hooks/useDocumentTitle'
import useMyId from '../../../hooks/useMyId'
import {getChangesHref, getHrefWithQueryParams} from '../../../routes'
import {stringifyId} from '../../../types'
import Loader from '../../common/Loader/Loader'
import Redirect from '../../common/Redirect/Redirect'
import SequenceLoader from '../../common/SequenceLoader/SequenceLoader'
import {
  ChangeContext,
  ChangeView,
  DEFAULT_CHANGES_LIST_CONTEXT,
} from '../../packages/Changes/Change/Change.context'
import {ChangeDetailsContext} from '../../packages/Changes/Change/ChangeDetails/ChangeDetails.context'
import ChangeDetailsTabsLazy from '../../packages/Changes/ChangeDetailsTabs/ChangeDetailsTabs.lazy'
import type {ChangeDetailsTabsProps} from '../../packages/Changes/ChangeDetailsTabs/ChangeDetailsTabs.types'
import {useChangesFilter, useCurrentPage} from '../../packages/Changes/Changes.hooks'
import {getChangesLocator} from '../../packages/Changes/Changes.utils'
import ChangesList from '../../packages/Changes/ChangesList/ChangesList'

import ChangesPageFilter from './ChangesPageFilter/ChangesPageFilter'
import ChangesPageHeader from './ChangesPageHeader'

import styles from './ChangesPage.css'

type Props = {
  locator: string
}

function ChangesPage({locator}: Props) {
  useDocumentTitle(`Changes - TeamCity`)

  const changesListContextValue = useMemo(
    () => ({
      ...DEFAULT_CHANGES_LIST_CONTEXT,
      changeView: ChangeView.OVERALL,
      withGraph: false,
    }),
    [],
  )

  const renderDetailsContent = useCallback(
    (props: ChangeDetailsTabsProps) => (
      <ChangeDetailsTabsLazy className={styles.changeDetailsTabs} {...props} />
    ),
    [],
  )
  const changeDetailsContextValue = useMemo(() => ({renderDetailsContent}), [renderDetailsContent])

  return (
    <div className={styles.page}>
      <ChangesPageHeader />
      <ChangesPageFilter className={styles.filter} />
      <ChangeContext.Provider value={changesListContextValue}>
        <ChangeDetailsContext.Provider value={changeDetailsContextValue}>
          <SequenceLoader loader={<Loader />}>
            <ChangesList seqKey="Changes" locator={locator} withPager />
          </SequenceLoader>
        </ChangeDetailsContext.Provider>
      </ChangeContext.Provider>
    </div>
  )
}

function ChangesPageWrapper() {
  const location = useLocation()
  const currentUserId = useMyId()
  const currentPage = useCurrentPage()
  const {filterOptions} = useChangesFilter()

  if (filterOptions == null || currentUserId == null) {
    return <Loader />
  }

  if (filterOptions.userId == null) {
    const href = getChangesHref()
    const to = getHrefWithQueryParams(location, href, {userId: stringifyId(currentUserId)})

    return <Redirect to={to} replace />
  }

  const locator = getChangesLocator({
    ...filterOptions,
    currentPage,
    baseLocator: '',
    unique: true,
  })

  return <ChangesPage locator={locator} />
}

export default memo(ChangesPageWrapper)
